import React, { useState } from 'react';
import {
  Image, Video, Music, FileText, Mail, Share2, File,
  Upload, Download, Eye, Trash2, CheckCircle, XCircle, Clock
} from 'lucide-react';

interface Asset {
  id: number;
  asset_type: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  r2_key?: string;
  review_status: 'pending' | 'approved' | 'rejected' | 'revision_requested';
  reviewer_feedback?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  metadata?: any;
  created_at: string;
}

interface AssetAttachmentsProps {
  assets: Asset[];
  onUpload: (file: File, assetType: string) => Promise<void>;
  onDelete: (assetId: number) => Promise<void>;
  onPreview: (asset: Asset) => void;
  onUpdateStatus: (assetId: number, status: string, feedback?: string) => Promise<void>;
  allowUpload?: boolean;
  allowDelete?: boolean;
  allowReview?: boolean;
}

const AssetAttachments: React.FC<AssetAttachmentsProps> = ({
  assets,
  onUpload,
  onDelete,
  onPreview,
  onUpdateStatus,
  allowUpload = true,
  allowDelete = true,
  allowReview = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState('image');

  const getAssetIcon = (type: string, mimeType?: string) => {
    if (type === 'image' || mimeType?.startsWith('image/')) {
      return <Image className="w-5 h-5" />;
    }
    if (type === 'video' || mimeType?.startsWith('video/')) {
      return <Video className="w-5 h-5" />;
    }
    if (type === 'audio' || type === 'podcast' || mimeType?.startsWith('audio/')) {
      return <Music className="w-5 h-5" />;
    }
    if (type === 'email') {
      return <Mail className="w-5 h-5" />;
    }
    if (type === 'social') {
      return <Share2 className="w-5 h-5" />;
    }
    if (type === 'pdf' || mimeType === 'application/pdf') {
      return <FileText className="w-5 h-5" />;
    }
    return <File className="w-5 h-5" />;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      case 'revision_requested':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Revision
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await onUpload(file, selectedAssetType);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      {allowUpload && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Upload Asset</h4>
          <div className="flex items-center gap-3">
            <select
              value={selectedAssetType}
              onChange={(e) => setSelectedAssetType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="document">Document</option>
              <option value="pdf">PDF</option>
              <option value="email">Email</option>
              <option value="social">Social Media</option>
              <option value="ads">Advertisement</option>
            </select>

            <label className="flex-1 cursor-pointer">
              <div className="flex items-center justify-center px-4 py-2 border border-blue-300 rounded-lg bg-white hover:bg-blue-50 text-blue-600">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Choose File'}
              </div>
              <input
                type="file"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
        </div>
      )}

      {/* Assets List */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-900">
          Attached Assets ({assets.length})
        </h4>

        {assets.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
            <File className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No assets attached yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                    {getAssetIcon(asset.asset_type, asset.mime_type)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="text-sm font-medium text-gray-900 truncate">
                        {asset.file_name}
                      </h5>
                      {getStatusBadge(asset.review_status)}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="capitalize">{asset.asset_type}</span>
                      <span>•</span>
                      <span>{formatFileSize(asset.file_size)}</span>
                      {asset.mime_type && (
                        <>
                          <span>•</span>
                          <span>{asset.mime_type}</span>
                        </>
                      )}
                    </div>
                    {asset.reviewer_feedback && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <span className="font-medium">Feedback:</span> {asset.reviewer_feedback}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onPreview(asset)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {allowDelete && (
                      <button
                        onClick={() => {
                          if (confirm('Delete this asset?')) {
                            onDelete(asset.id);
                          }
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Review Actions */}
                {allowReview && asset.review_status === 'pending' && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onUpdateStatus(asset.id, 'approved')}
                        className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          const feedback = prompt('Reason for rejection:');
                          if (feedback) {
                            onUpdateStatus(asset.id, 'rejected', feedback);
                          }
                        }}
                        className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        <XCircle className="w-4 h-4 inline mr-1" />
                        Reject
                      </button>
                      <button
                        onClick={() => {
                          const feedback = prompt('What needs to be revised?');
                          if (feedback) {
                            onUpdateStatus(asset.id, 'revision_requested', feedback);
                          }
                        }}
                        className="flex-1 px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                      >
                        Request Revision
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetAttachments;
